/**
 * VolumeViewport3D - 3D volume rendering viewport.
 *
 * Renders the "rendering-volume" slot directly using a VTK volume pipeline
 * (vtkVolumeMapper + vtkVolume + transfer functions).
 *
 * Uses DisplayFeatureManager for additional overlay objects (centerlines, spheres, etc.).
 */

import vtkVolume from '@kitware/vtk.js/Rendering/Core/Volume';
import vtkVolumeMapper from '@kitware/vtk.js/Rendering/Core/VolumeMapper';

import { BaseViewport } from './base-viewport';
import type { ViewportType, ViewportVtkContext, VolumeViewportOptions } from './types';
import type { ServiceContext } from '../core/types';
import type { Volume } from '../domain/volume';
import type { Color3DPresetId } from '../services/color-3d-presets';
import { Color3DService } from '../services/color-3d-service';
import type { VolumeTransferFunctions } from '../services/color-3d-service';
import { ViewportDataService } from '../services/viewport-data-service';

/**
 * Default perspective view angle (degrees). Matches VTK's camera default and
 * RotateTool's DEFAULT_VIEW_ANGLE — keeping them in agreement is what makes
 * rotation sensitivity return to its baseline after a camera reset.
 */
const DEFAULT_VIEW_ANGLE = 30;

export class VolumeViewport3D extends BaseViewport {
    private vtkVolume: vtkVolume | null = null;
    private vtkVolumeMapper: vtkVolumeMapper | null = null;
    private currentVolume: Volume | null = null;
    /** Transfer functions built by Color3DService — this viewport owns their lifetime. */
    private transferFunctions: VolumeTransferFunctions | null = null;
    private readonly pinnedPresetId: Color3DPresetId | null;

    constructor(
        id: string,
        type: ViewportType,
        vtkContext: ViewportVtkContext,
        context: ServiceContext,
        options?: VolumeViewportOptions
    ) {
        super(id, type, vtkContext, context, options);
        this.pinnedPresetId = options?.color3DPresetId ?? null;
        this.subscribeToVolumeData();
        this.subscribeToColor3DService();
    }

    // ========================================================================
    // ViewportDataService subscription
    // ========================================================================

    private subscribeToVolumeData(): void {
        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );

        const existing = dataService.getSlot('rendering-volume');
        if (existing) {
            this.setVolume(existing);
        }

        this.unsubscribers.push(
            dataService.subscribeToSlot('rendering-volume', (volume) => {
                this.setVolume(volume);
            })
        );
    }

    // ========================================================================
    // Volume rendering pipeline
    // ========================================================================

    private setVolume(volume: Volume): void {
        // Dispose previous pipeline if replacing volume
        this.disposeVolumePipeline();

        // Create VTK pipeline
        this.vtkVolumeMapper = vtkVolumeMapper.newInstance();
        this.vtkVolumeMapper.setInputData(volume.imageData);

        this.vtkVolume = vtkVolume.newInstance();
        this.vtkVolume.setMapper(this.vtkVolumeMapper);

        this.applyTransferFunction();

        // Add to renderer and set up the camera
        const renderer = this.getRenderer(0);
        renderer.addVolume(this.vtkVolume);
        this.currentVolume = volume;
        this.initializeCamera(volume);
        this.render();
    }

    /** Restore the camera to the default framing computed at load time. */
    resetCameraToDefault(): void {
        if (!this.currentVolume) return;
        this.initializeCamera(this.currentVolume);
        this.render();
    }

    /**
     * Apply this viewport's 3D color preset — the pinned profile when set,
     * otherwise the active one — (transfer functions + material) to the volume.
     * The previous transfer functions are deleted once replaced.
     * Presets may also override the viewport background (virtual angio renders
     * dark vessels on a light background); absent an override, the viewport's
     * own background is restored.
     */
    private applyTransferFunction(): void {
        if (!this.vtkVolume) return;

        const colorService = this.context.services.getService<Color3DService>(Color3DService.SERVICE_NAME);

        const previous = this.transferFunctions;
        this.transferFunctions = colorService.applyToVolumeProperty(
            this.vtkVolume.getProperty(),
            this.pinnedPresetId ?? undefined
        );
        previous?.colorFunction.delete();
        previous?.opacityFunction.delete();

        const preset = this.pinnedPresetId
            ? colorService.getPreset(this.pinnedPresetId)
            : colorService.getActivePreset();
        this.getRenderer(0).setBackground(...(preset.background ?? this.baseBackgroundColor));
    }

    // ========================================================================
    // Color3DService subscription
    // ========================================================================

    private subscribeToColor3DService(): void {
        // A pinned viewport keeps its profile — preset changes elsewhere (user
        // selection, C-arm draft override) must not recolor it.
        if (this.pinnedPresetId) return;

        const colorService = this.context.services.getService<Color3DService>(Color3DService.SERVICE_NAME);

        this.unsubscribers.push(
            colorService.subscribe(colorService.EVENTS.COLOR_3D_CHANGED, () => {
                if (!this.vtkVolume) return;
                this.applyTransferFunction();
                this.render();
            }),
            // Window/level only moves the control points — rewrite them in the
            // functions already plugged into the volume property rather than
            // rebuilding the whole profile on every drag frame
            colorService.subscribe(colorService.EVENTS.WINDOW_LEVEL_CHANGED, () => {
                if (!this.vtkVolume || !this.transferFunctions) return;
                colorService.refreshTransferFunctions(this.transferFunctions);
                this.render();
            })
        );
    }

    // ========================================================================
    // Camera setup
    // ========================================================================

    /**
     * Position the camera so that:
     * - Z is vertical (view-up = [0, 0, 1])
     * - We look from the front (along -Y toward the volume center)
     * - The entire volume fits inside the viewport
     */
    private initializeCamera(volume: Volume): void {
        const bounds = volume.bounds;
        const center = volume.center;
        const camera = this.getRenderer(0).getActiveCamera();

        // Restore the default field of view. Zoom (perspective) mutates the view
        // angle, so a reset must reset it too — otherwise the stale angle persists
        // and rotation sensitivity (which scales by view angle) stays wrong.
        camera.setViewAngle(DEFAULT_VIEW_ANGLE);

        // Extent along each axis
        const extentX = bounds[1] - bounds[0];
        const extentY = bounds[3] - bounds[2];
        const extentZ = bounds[5] - bounds[4];

        // Distance from the center to fully contain the volume in the view.
        // Compute the required distance to fit both X and Z extents independently
        // given the camera's FOV and viewport aspect ratio, then take the max.
        // The camera must also be at least extentY/2 away to sit in front of the volume.
        const halfAngleRad = ((DEFAULT_VIEW_ANGLE / 2) * Math.PI) / 180;

        const canvas = this.getCanvas();
        const cw = canvas?.clientWidth || 0;
        const ch = canvas?.clientHeight || 0;
        const aspectRatio = cw > 0 && ch > 0 ? cw / ch : 1;
        const horizontalHalfAngle = Math.atan(Math.tan(halfAngleRad) * aspectRatio);

        const distFromX = extentX / 2 / Math.tan(horizontalHalfAngle);
        const distFromZ = extentZ / 2 / Math.tan(halfAngleRad);
        const fittingDist = Math.max(distFromX, distFromZ);
        const clearanceDist = extentY / 2;
        const distance = Math.max(fittingDist, clearanceDist) * 1.1;

        camera.setFocalPoint(center[0], center[1], center[2]);
        camera.setPosition(center[0], center[1] - distance, center[2]);
        camera.setViewUp(0, 0, 1);

        this.updateCameraClippingRange();
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    private disposeVolumePipeline(): void {
        if (this.vtkVolume) {
            const renderer = this.getRenderer(0);
            renderer.removeVolume(this.vtkVolume);
        }

        this.vtkVolume?.delete();
        this.vtkVolumeMapper?.delete();
        this.transferFunctions?.colorFunction.delete();
        this.transferFunctions?.opacityFunction.delete();
        this.vtkVolume = null;
        this.vtkVolumeMapper = null;
        this.transferFunctions = null;
        this.currentVolume = null;
    }

    override dispose(): void {
        this.disposeVolumePipeline();
        super.dispose();
    }
}
